import { scopedApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { CatalogItemGroup, CatalogItemRecord } from '@/types/catalog';
import type { CustomFieldStoredValue } from '@/types/customField';
import type { EndpointResponse, ErrorResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../requestOptions';

export type CatalogFieldValues = Readonly<Record<string, CustomFieldStoredValue>>;

/** A variant created with its product. kitchensink checks each key under `fields` against the catalog's custom fields. */
export type CatalogVariantCreate = Readonly<{
  name: string;
  sku: string;
  upc?: string;
  group: 'variant';
  fields: CatalogFieldValues;
  pricing_item?: Readonly<{ price: Readonly<{ amount: number; currency: 'USD' }> }>;
}>;

/** The POST /catalog body. kitchensink binds it strictly, so an unknown key is refused: the body holds only these. */
export type CatalogItemCreate = Readonly<{
  name: string;
  sku: string;
  group: CatalogItemGroup;
  upc?: string;
  ean?: string;
  gtin?: string;
  isbn?: string;
  mpn?: string;
  fields?: CatalogFieldValues;
  options?: readonly string[];
  variants?: readonly CatalogVariantCreate[];
}>;

type CreatedReply = Exclude<EndpointResponse<CatalogItemRecord>, ErrorResponse>;

/** Creates a catalog item, with a product's variants in the same transaction. */
export const createCatalogItem = async (
  scope: RequestScope,
  { data }: { data: CatalogItemCreate },
  options: WriteRequestOptions
): Promise<EndpointResponse<CatalogItemRecord>> => {
  try {
    const response = await scopedApi(scope).post<CreatedReply>('catalog', data, options);
    // Only handleAxiosError marks a failure, with the HTTP status. A 2xx body can't mark itself one, and the reply keeps
    // the HTTP status, so whatever the body holds, the caller checks it as a success that may be malformed.
    return { ...response.data, isError: false, status: response.status };
  } catch (error) {
    return utils.error.handleAxiosError(error, { redactRequestData: true });
  }
};
